import React from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, Building2 } from 'lucide-react';
import { orgFeatures } from '../../data/mock';
import useReveal from '../../hooks/useReveal';

/**
 * For colleges, universities and employers.
 *
 * A second audience with a different job to be done: not "improve my English"
 * but "prove and track English across a cohort". Kept visually calmer than the
 * consumer sections so it reads as a considered institutional offer.
 */
const OrganizationsSection = () => {
  const ref = useReveal({ stagger: 90 });

  return (
    <section ref={ref} className="section">
      <div
        className="reveal overflow-hidden rounded-[28px]"
        style={{
          background: 'linear-gradient(135deg, #F7F9FC 0%, #EEF3FA 100%)',
          border: '1px solid var(--border-light)'
        }}
      >
        <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:p-14">
          {/* ------------------------------ Copy ----------------------------- */}
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[0.75rem] font-semibold"
              style={{ background: '#fff', color: 'var(--tenses-blue)', boxShadow: 'var(--shadow-xs)' }}
            >
              <Building2 className="h-3.5 w-3.5" />
              For institutions
            </span>

            <h2 className="heading-2 mt-5" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>
              Run English proficiency
              <br />
              across a whole batch
            </h2>

            <p className="body-medium mt-4 max-w-[440px]">
              Onboard a cohort in minutes, set your own mock assessments, and watch
              accuracy climb across every group - from one dashboard.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">
                Talk to us
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/pricing" className="btn-secondary">
                View plans
              </Link>
            </div>
          </div>

          {/* ---------------------------- Features --------------------------- */}
          <div className="grid gap-4 sm:grid-cols-2">
            {orgFeatures.map((feature) => {
              const Icon = LucideIcons[feature.icon] || LucideIcons.Circle;
              return (
                <div
                  key={feature.title}
                  className="reveal rounded-2xl bg-white p-5"
                  style={{ border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-xs)' }}
                >
                  <span
                    className="mb-3.5 grid h-10 w-10 place-items-center rounded-xl"
                    style={{ background: 'rgba(52,89,164,0.09)', color: 'var(--tenses-blue)' }}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <h3 className="text-[0.98rem] font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {feature.title}
                  </h3>
                  <p className="mt-1.5 text-[0.88rem] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrganizationsSection;
